import { css } from "../../css/util.compose-css";

import { units } from "../units";

type Config = {
  target?: string;
  focusWithin?: boolean;
  pseudo?: "::before" | "::after";

  color?: string;

  width?: string;
  offset?:
    | string
    | {
        x: string | { left: string; right: string };
        y: string | { top: string; bottom: string };
      };
  borderRadius?: string;
};

/**
 * Wraps an element with an outline that is only visible when :focus-visible is
 * true (falling back to :focus for browsers not supporting :focus-visible).
 *
 * Furthermore, by using a custom element we can easily allow for rounded
 * borders, custom offsets and more with a larger degree of freedom than simply
 * using the outline property.
 *
 * NB: In case the outline should be enabled on a specific child once the
 * parent is focused, then the `target` parameter can be used to
 * specify the relevant child.
 *
 * @example when button is focused the child will receive a outline around it.
 *
 * // jsx
 * <button className="parent">
 *    <div className="outline" />
 * </button>
 *
 * // css
 * .parent {
 *    ${outline({ color: "green", target: "& > .outline" })};
 *
 *    width: 200px;
 * }
 *
 * .child {
 *    width: 50px;
 * }
 */
export const outline = ({
  target = "&",
  focusWithin = false,
  pseudo = "::after",

  color = "#757783",
  width = units.px(2),
  borderRadius = units.px(4),
  offset = units.px(0),
}: Config = {}): string => css`
  *:where(${target}) {
    position: relative;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  *:where(${focusWithin ? "&:has(*:focus)" : "&:focus"}) {
    outline: none;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  *:where(${focusWithin ? "&:has(*:focus-visible)" : "&:focus-visible"}) {
    outline: none;
  }

  *:where(
      ${focusWithin
          ? target.replace(/&/g, "&:has(*:focus)")
          : target.replace(/&/g, "&:focus")}
    )${pseudo} {
    content: "";

    position: absolute;
    inset: calc(
        -1 * ${typeof offset === "string" ? offset : typeof offset.y === "string" ? offset.y : offset.y.top}
      )
      calc(
        -1 * ${typeof offset === "string" ? offset : typeof offset.x === "string" ? offset.x : offset.x.right}
      )
      calc(
        -1 * ${typeof offset === "string" ? offset : typeof offset.y === "string" ? offset.y : offset.y.bottom}
      )
      calc(
        -1 * ${typeof offset === "string" ? offset : typeof offset.x === "string" ? offset.x : offset.x.left}
      );
    z-index: 10;

    border: ${width} solid ${color};
    border-radius: ${borderRadius};

    pointer-events: none;
  }

  *:where(
      ${focusWithin
          ? target.replace(/&/g, "&:has(*:focus:not(:focus-visible))")
          : target.replace(/&/g, "&:focus:not(:focus-visible)")}
    )${pseudo} {
    content: none;
  }
`;
